/**
 * The scam notice, composed from the gateway's signal keys (UX spec v3 §6.7,
 * the clause map in §8.1 — copied verbatim; ruling 23 Sep, DCTA-71 addendum;
 * DCTA-155 §F.2 row 21 for the two questions, the urgency clause and two new keys).
 *
 * The client never writes a reason the gateway did not return: one clause per
 * `POL-SCAM-*` key, in the gateway's order, joined with a space. A signal that
 * describes the customer rather than the payment (`POL-SCAM-AGE-65`) weighs but
 * says nothing. A key the map does not hold adds nothing — it still weighed in
 * the level the gateway chose.
 */
import { sgDay } from "../lib/format.js";

export interface ScamInputs {
  /** The step's amount as the card prints it: "S$2,500.00". */
  readonly amount: string;
  /** The payee as the card names it: the nickname, else the registered name. */
  readonly name: string;
  /** The release time as the card prints it ("24 Sep 09:41"), for level hold. */
  readonly release: string | undefined;
  /**
   * DCTA-120 (§F.2 6e): the payee is the PayNow directory's (`payee.source = "paynow_lookup"`), paid once
   * and never saved — not "a new payee": "You haven't paid … before", and the customer is asked to check the name.
   */
  readonly adHoc?: boolean | undefined;
  /** F43: the bank's time of this card (its payload's `created_at`) — what "Earlier today" and "Yesterday" are measured from. */
  readonly now?: string | undefined;
}

/**
 * F43 (QA D3, §F.2 23b; backend B53, not built yet — this is the shape the app reads): a signal, or a Yes to one of the two
 * questions, that the bank carried from an earlier conversation, and when it was heard.
 */
export interface Heard {
  readonly signal?: string;
  readonly question_id?: "contact" | "secrecy";
  readonly heard: "earlier_conversation";
  readonly heard_at: string;
}

export interface ScamNotice {
  readonly lead: string;
  /** §F.2 6e: the sentence after the clauses that asks the customer to check whom they pay (an ad-hoc payee), or "". */
  readonly check: string;
  readonly questions: readonly string[];
  readonly after: string;
  /** Level question: the sign button waits until both answers are No. */
  readonly signBlocked: boolean;
}

const CLAUSE: Readonly<Record<string, ((i: ScamInputs) => string) | null>> = {
  "POL-SCAM-NEW-PAYEE": (i) => (i.adHoc === true ? `You haven't paid ${i.name} before.` : `${i.name} is a new payee.`),
  "POL-SCAM-UNUSUAL-AMOUNT": (i) => `${i.amount} is far more than you usually send.`,
  "POL-SCAM-AGE-65": null,
  // §F.2 21m: the questions' word for one thing (was ‘…urgent or private.’).
  "POL-SCAM-URGENCY": () => "You said this is urgent or secret.",
  "POL-SCAM-AUTHORITY-CLAIM": () => "You mentioned an officer, the police or an official.",
  "POL-SCAM-CRYPTO-ONRAMP": () => "This goes to a crypto exchange.",
  // §F.2 21k, 21l (policy 1.5.0, DCTA-153): true for every word of each family. A signal the bank carried from an
  // earlier turn of the errand has the same clause — past tense, true of either (21n).
  "POL-SCAM-SAFE-ACCOUNT": () => "You mentioned a safe account, or verifying your account.",
  "POL-SCAM-REMOTE-ACCESS": () => "You mentioned someone seeing or controlling your screen.",
  // DCTA-184 FE spec (docs/specs/fe-scam-clauses-dcta184.md), verbatim the bank's (lab/dcta src/tts/builders.ts › CLAUSE,
  // src/resolver/scam-payee.ts): policy 1.6.0's evasion story (DCTA-177 RT-174-03) and 1.7.0's caller (DCTA-184 D1).
  "POL-SCAM-EVASION": () => "You mentioned getting past the bank's checks.",
  "POL-SCAM-CALLER-INSTRUCTION": () => "You mentioned someone on a call telling you what to do.",
};

/**
 * F43 (§F.2 23b): the same facts, said as from an earlier conversation — never "You said…" in a new chat (23c). {When} is
 * "Earlier today" or "Yesterday", by the Singapore date it was heard.
 */
const EARLIER: Readonly<Record<string, string>> = {
  "POL-SCAM-URGENCY": "you said this is urgent or secret.",
  "POL-SCAM-AUTHORITY-CLAIM": "you mentioned an officer, the police or an official.",
  "POL-SCAM-SAFE-ACCOUNT": "you mentioned a safe account, or verifying your account.",
  "POL-SCAM-REMOTE-ACCESS": "you mentioned someone seeing or controlling your screen.",
  // DCTA-184 FE spec: the same two, as from an earlier conversation (the bank's EARLIER_WORDS).
  "POL-SCAM-EVASION": "you mentioned getting past the bank's checks.",
  "POL-SCAM-CALLER-INSTRUCTION": "you mentioned someone on a call telling you what to do.",
};
const EARLIER_YES: Readonly<Record<"contact" | "secrecy", string>> = {
  contact: "you told us someone you haven't met in person asked you to make a payment.",
  secrecy: "you told us you were told to keep a payment secret, or to hurry.",
};

function whenOf(heardAt: string, now: string | undefined): string {
  if (now === undefined) return "Earlier today";
  return sgDay(heardAt) === sgDay(now) ? "Earlier today" : "Yesterday";
}

/** §F.2 6e (the Pay form's sentence, §E.6): what a one-off payment to a PayNow number asks of the customer. */
export const CHECK_PAYEE = "Check this is who you mean to pay — money sent to the wrong person is hard to get back.";

/**
 * §F.2 21b, 21c (DCTA-155): one fact each, answerable from memory, Yes always means stop; the customer never judges
 * their own situation. The server knows only the ids (`contact`, `secrecy`) — B36 moves its set to `scam-q/2` so the
 * audit record points at these words.
 */
export const SCAM_QUESTIONS = [
  "Did someone you haven't met in person ask you to make this payment?",
  "Were you told to keep this payment secret, or to hurry?",
] as const;

/**
 * The gateway's ids for the two questions, in the same order (DCTA-74 ask f,
 * `scam-q/1`): the WORDING is the screen's, what an answer means is the server's.
 */
export const SCAM_QUESTION_IDS = ["contact", "secrecy"] as const;

/** One answer as the confirm route takes it, beside the assertion. */
export interface ScamAnswer {
  readonly step_id: string;
  readonly question_id: (typeof SCAM_QUESTION_IDS)[number];
  readonly answer: "no" | "yes";
}

export function composeScamNotice(
  intervention: { readonly level: "warning" | "question" | "hold"; readonly signals: readonly string[]; readonly heard?: readonly Heard[] | undefined },
  inputs: ScamInputs,
): ScamNotice {
  const heard = intervention.heard ?? [];
  const earlier = (key: string): string | undefined => {
    const h = heard.find((x) => x.signal === key && x.heard === "earlier_conversation");
    const words = EARLIER[key];
    return h === undefined || words === undefined ? undefined : `${whenOf(h.heard_at, inputs.now)} ${words}`;
  };
  const yeses = heard
    .filter((x) => x.question_id !== undefined && x.heard === "earlier_conversation")
    .map((x) => `${whenOf(x.heard_at, inputs.now)} ${EARLIER_YES[x.question_id as "contact" | "secrecy"]}`);
  const lead = [...intervention.signals.map((key) => earlier(key) ?? CLAUSE[key]?.(inputs)), ...yeses]
    .filter((c): c is string => typeof c === "string")
    .join(" ");
  const hold = intervention.level === "hold" && inputs.release !== undefined;
  return {
    lead,
    check: inputs.adHoc === true && intervention.signals.includes("POL-SCAM-NEW-PAYEE") ? CHECK_PAYEE : "",
    questions: intervention.level === "question" ? [...SCAM_QUESTIONS] : [],
    after: hold ? `If you sign, it goes out on ${inputs.release as string}, and you can cancel it any time before then.` : "",
    signBlocked: intervention.level === "question",
  };
}

/**
 * P1-4 (DCTA-192): does this intervention put words on the card? A signal that describes the customer (`POL-SCAM-AGE-65`)
 * or a key the clause map does not hold weighs in the level but says nothing; the questions always say something. Read
 * before any card-level filtering (a new payee's 12-hour wait says the clause on its held notice instead).
 */
export function drawsClause(intervention: Parameters<typeof composeScamNotice>[0]): boolean {
  if (intervention.level === "question") return true;
  return composeScamNotice(intervention, { amount: "", name: "", release: undefined }).lead !== "";
}

export type ScamQuestionId = (typeof SCAM_QUESTION_IDS)[number];

/** §F.2 21g: what the question answered Yes is a sign of, by its id. */
const SIGN_OF: Readonly<Record<ScamQuestionId, string>> = {
  contact: "Being asked to pay by someone you haven't met in person is a common sign of a scam.",
  secrecy: "Being told to keep a payment secret, or to hurry, is a common sign of a scam.",
};

/**
 * §F.2 21f, 21g (DCTA-155, F24): the paused end after a Yes — the safe exit, never a signature (the bank cancels the
 * request). The lead says what is true now: the card cannot be taken up again, so it promises nothing. Then the
 * sentence for the question answered Yes, then one line for either. Its ways on stay as built (21h).
 */
export function pausedNotice(question: ScamQuestionId): { readonly lead: string; readonly rest: string } {
  return { lead: PAUSED_LEAD, rest: ` ${SIGN_OF[question]} ${PAUSED_AFTER}` };
}

const PAUSED_LEAD = "Paused. Nothing was sent.";
const PAUSED_AFTER = "Talk to us, or to someone you trust, before you pay.";

/**
 * DCTA-154 F27 (b) (backend DCTA-157 B38; Claude: reuse 21f/21g): the bank's own paused words for a payment refused because
 * of a Yes carried for that payee — its message (21f's lead, 21g's sentence) and its next step, as the card's paused
 * notice reads them. 21f's lead is bolded only when the bank leads with it; the words are the bank's, as sent.
 */
export function bankPausedNotice(r: { readonly customer_message: string; readonly next_steps?: readonly string[] }): { readonly lead: string; readonly rest: string } {
  const next = (r.next_steps ?? []).filter((s) => s.trim() !== "");
  const tail = next.length === 0 ? "" : ` ${next.join(" ")}`;
  const message = r.customer_message;
  return message.startsWith(PAUSED_LEAD) ? { lead: PAUSED_LEAD, rest: `${message.slice(PAUSED_LEAD.length)}${tail}` } : { lead: "", rest: `${message}${tail}` };
}
